import { bit_decombine, order2nside, pixcoord2vec_nest, query_disc_inclusive_nest } from '@hscmap/healpix';
import type { Vec3 } from '../../lib/math';
import { GL, GLSL_COMMON, Program } from '../gl';

// A photographic sky when zoomed in: the Digitized Sky Survey in colour (DSS2), served by CDS
// Strasbourg as HiPS tiles (IVOA HiPS 1.0: HEALPix nested cells, 512 px each). Only the tiles in view
// are fetched, finest level for the zoom first; until one arrives, the best tile already on hand (or
// the whole-sky preview) stands in for it, cropped to the right corner, so the view sharpens in place.
// Each screen area is drawn once, so the result can simply be added to the sky.

const MIRRORS = [
  'https://alasky.cds.unistra.fr/DSS/DSSColor',
  'https://alaskybis.cds.unistra.fr/DSS/DSSColor',
  'https://stpubdata.s3.us-east-1.amazonaws.com/mast/skybackgrounds/DSSColor',
];
const MAX_ORDER = 9;
const MIN_ORDER = 3;
const TILE = 512;
const ALLSKY_COLS = 27, ALLSKY_W = 1728, ALLSKY_H = 1856, THUMB = 64;

const VS = /* glsl */ `#version 300 es
precision highp float;
${GLSL_COMMON}
layout(location = 0) in vec3 a_dir;
layout(location = 1) in vec2 a_uv;
uniform vec4 u_uvRect; // offset.xy, scale.zw into the texture
out vec2 v_uv;
out float v_valid;
out float v_alt;
out vec3 v_dir;
void main() {
  v_dir = a_dir;
  vec3 e = u_eqjToEnu * a_dir;
  vec3 p = projectCam(u_enuToCam * refractEnu(e));
  v_uv = u_uvRect.xy + a_uv * u_uvRect.zw;
  v_valid = p.z;
  v_alt = e.z;
  gl_Position = vec4(p.xy, 0.0, 1.0);
}`;

const FS = /* glsl */ `#version 300 es
precision highp float;
in vec2 v_uv;
in float v_valid;
in float v_alt;
in vec3 v_dir;
uniform sampler2D u_tex;
uniform float u_gain;
uniform vec4 u_avoid; // a bright body's direction (xyz) and the cosine of how far to keep clear (w)
out vec4 o;
void main() {
  if (v_valid < 0.999) discard;
  vec3 c = texture(u_tex, v_uv).rgb;
  // Lift the survey's grey sky background to black and keep the faint glow subtle.
  c = pow(max(c - 0.075, 0.0) / 0.925, vec3(1.35));
  // Air mass: fainter and redder towards the horizon, like the rest of the sky.
  float h = max(v_alt, 0.0);
  float airmass = 1.0 / (h + 0.025 * exp(-11.0 * h));
  vec3 ext = exp(-vec3(0.22, 0.28, 0.4) * (airmass - 1.0));
  // Faint photographic stars make no sense in the glare right beside the Moon.
  float clear = u_avoid.w < 1.0 ? 1.0 - smoothstep(u_avoid.w, 1.0 - (1.0 - u_avoid.w) * 0.04, dot(normalize(v_dir), u_avoid.xyz)) : 1.0;
  o = vec4(c * ext * u_gain * clear * smoothstep(-0.02, 0.02, v_alt), 0.0);
}`;

interface Mesh { vao: WebGLVertexArrayObject; buf: WebGLBuffer; count: number; used: number }
interface Tex { tex: WebGLTexture; used: number }

/** Grid resolution per tile side: finer for big (low-order) cells, which bend more across the sky. */
const gridFor = (order: number) => (order <= 4 ? 16 : order === 5 ? 8 : order === 6 ? 4 : 2);
const tileUrl = (base: string, order: number, n: number) => `${base}/Norder${order}/Dir${Math.floor(n / 10000) * 10000}/Npix${n}.jpg`;

export class SurveyLayer {
  private prog: Program;
  private ibo: Map<number, { buf: WebGLBuffer; count: number }> = new Map();
  private meshes = new Map<string, Mesh>();
  private textures = new Map<string, Tex>();
  private pending = new Map<string, AbortController>();
  private failed = new Map<string, number>();
  private allsky: WebGLTexture | null = null;
  private allskyLoading = false;
  private mirror = 0;
  private frame = 0;
  /** Whether anything is still loading (so the engine keeps drawing at full rate). */
  busy = false;
  private readonly maxTextures: number;

  constructor(private gl: GL, mobile: boolean) {
    this.prog = new Program(gl, VS, FS, 'survey');
    this.maxTextures = mobile ? 72 : 180;
  }

  /** Pick the HEALPix order whose tile pixels roughly match the screen's (CSS) pixels. */
  static orderFor(fovDeg: number, shortCssPx: number, dpr: number): number {
    const arcminPerPx = (fovDeg * 60) / (shortCssPx * Math.min(dpr, 1.5));
    return Math.max(MIN_ORDER, Math.min(MAX_ORDER, Math.ceil(Math.log2(6.871 / arcminPerPx))));
  }

  private indexBuffer(n: number) {
    let ib = this.ibo.get(n);
    if (!ib) {
      const idx: number[] = [];
      for (let j = 0; j < n; j++)
        for (let i = 0; i < n; i++) {
          const a = j * (n + 1) + i;
          idx.push(a, a + 1, a + n + 1, a + 1, a + n + 2, a + n + 1);
        }
      const buf = this.gl.createBuffer()!;
      this.gl.bindBuffer(this.gl.ELEMENT_ARRAY_BUFFER, buf);
      this.gl.bufferData(this.gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(idx), this.gl.STATIC_DRAW);
      this.ibo.set(n, (ib = { buf, count: idx.length }));
    }
    return ib;
  }

  /** The cell's vertex grid: (u, v) run along the image's columns (north-west) and rows (north-east). */
  private mesh(order: number, n: number): Mesh {
    const key = `${order}/${n}`;
    let m = this.meshes.get(key);
    if (m) {
      m.used = this.frame;
      return m;
    }
    const gl = this.gl;
    const g = gridFor(order);
    const nside = order2nside(order);
    const data = new Float32Array((g + 1) * (g + 1) * 5);
    let k = 0;
    for (let j = 0; j <= g; j++)
      for (let i = 0; i <= g; i++) {
        const u = i / g, v = j / g;
        const d = pixcoord2vec_nest(nside, n, v, u);
        data.set([d[0], d[1], d[2], u, v], k);
        k += 5;
      }
    const vao = gl.createVertexArray()!;
    gl.bindVertexArray(vao);
    const buf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 20, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 20, 12);
    const ib = this.indexBuffer(g);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib.buf);
    gl.bindVertexArray(null);
    m = { vao, buf, count: ib.count, used: this.frame };
    this.meshes.set(key, m);
    if (this.meshes.size > 600) this.evict(this.meshes, 450, (x) => { gl.deleteVertexArray(x.vao); gl.deleteBuffer(x.buf); });
    return m;
  }

  /** Free the least recently drawn entries down to `keep`, but never one drawn in the last two frames. */
  private evict<T extends { used: number }>(map: Map<string, T>, keep: number, free: (x: T) => void) {
    const old = [...map.entries()].filter(([, v]) => v.used < this.frame - 1).sort((a, b) => a[1].used - b[1].used);
    for (const [k, v] of old.slice(0, Math.max(0, map.size - keep))) {
      free(v);
      map.delete(k);
    }
  }

  private upload(img: ImageBitmap): WebGLTexture {
    const gl = this.gl;
    const t = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB8, gl.RGB, gl.UNSIGNED_BYTE, img);
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    img.close();
    return t;
  }

  private async fetchImage(path: (base: string) => string, signal?: AbortSignal): Promise<ImageBitmap> {
    // Try the preferred mirror, then the others; stick with whichever answers.
    let last: unknown;
    for (let k = 0; k < MIRRORS.length; k++) {
      const m = (this.mirror + k) % MIRRORS.length;
      try {
        const r = await fetch(path(MIRRORS[m]), { signal, mode: 'cors' });
        if (!r.ok) throw new Error(String(r.status));
        const bmp = await createImageBitmap(await r.blob(), { colorSpaceConversion: 'none' });
        this.mirror = m;
        return bmp;
      } catch (e) {
        if ((e as Error).name === 'AbortError') throw e;
        last = e;
      }
    }
    throw last;
  }

  private loadAllsky() {
    if (this.allskyLoading) return;
    this.allskyLoading = true;
    this.fetchImage((b) => `${b}/Norder3/Allsky.jpg`)
      .then((img) => (this.allsky = this.upload(img)))
      .catch(() => setTimeout(() => (this.allskyLoading = false), 30000));
  }

  private request(order: number, n: number) {
    const key = `${order}/${n}`;
    if (this.textures.has(key) || this.pending.has(key)) return;
    if ((this.failed.get(key) ?? 0) > Date.now()) return;
    const ac = new AbortController();
    this.pending.set(key, ac);
    this.fetchImage((b) => tileUrl(b, order, n), ac.signal)
      .then((img) => {
        this.textures.set(key, { tex: this.upload(img), used: this.frame });
        const budget = Math.max(this.maxTextures, this.inView + 12);
        if (this.textures.size > budget) this.evict(this.textures, budget - 8, (x) => this.gl.deleteTexture(x.tex));
      })
      .catch((e) => {
        if ((e as Error).name !== 'AbortError') this.failed.set(key, Date.now() + 60000);
      })
      .finally(() => this.pending.delete(key));
  }

  /**
   * Draw the survey over the sky. `centre` is the view direction in J2000 equatorial coordinates and
   * `radius` the angle from it to the corner of the screen.
   */
  /** How many cells the last frame needed (the texture budget never goes below this). */
  private inView = 0;

  /**
   * Draw the survey over the sky. With `fetch` false (the photo only just fading in, many cells in view),
   * nothing new is downloaded and the whole-sky preview stands in.
   */
  draw(setCommon: (p: Program) => void, o: { centre: Vec3; radius: number; order: number; gain: number; fetch?: boolean; avoid?: [number, number, number, number] }) {
    this.frame++;
    this.loadAllsky();
    const gl = this.gl;
    const K = o.order;
    const nside = order2nside(K);
    const cells: number[] = [];
    // The query already includes every cell that touches the disc.
    query_disc_inclusive_nest(nside, o.centre as [number, number, number], Math.min(Math.PI, o.radius * 1.03), (n) => cells.push(n));
    this.inView = cells.length;
    // Nearest to the middle first, a few at a time; forget requests that have left the view.
    const want = new Set(o.fetch === false ? [] : cells.map((n) => `${K}/${n}`));
    for (const [key, ac] of this.pending) if (!want.has(key)) ac.abort();
    const byDist = cells
      .filter((n) => !this.textures.has(`${K}/${n}`))
      .map((n) => {
        const c = pixcoord2vec_nest(nside, n, 0.5, 0.5);
        return { n, d: -(c[0] * o.centre[0] + c[1] * o.centre[1] + c[2] * o.centre[2]) };
      })
      .sort((a, b) => a.d - b.d);
    for (const { n } of o.fetch === false ? [] : byDist) {
      if (this.pending.size >= 6) break;
      this.request(K, n);
    }
    this.busy = this.pending.size > 0;

    const p = this.prog.use();
    setCommon(p);
    const av = o.avoid ?? [0, 0, 1, 1];
    p.f1('u_gain', o.gain).i1('u_tex', 3).f4('u_avoid', av[0], av[1], av[2], av[3]);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE);
    gl.activeTexture(gl.TEXTURE3);
    for (const n of cells) {
      // The best texture on hand: the tile itself, else the nearest ancestor, else the whole-sky preview.
      let tex: WebGLTexture | null = null;
      let rect: [number, number, number, number] = [0, 0, 1, 1];
      let texW = TILE, texH = TILE;
      for (let d = 0; d <= K - MIN_ORDER; d++) {
        const t = this.textures.get(`${K - d}/${Math.floor(n / 4 ** d)}`);
        if (!t) continue;
        t.used = this.frame;
        tex = t.tex;
        const { x, y } = bit_decombine(n % 4 ** d);
        const s = 1 / 2 ** d;
        rect = [y * s, x * s, s, s];
        break;
      }
      if (!tex && this.allsky) {
        const d = K - MIN_ORDER;
        const parent = Math.floor(n / 4 ** d);
        const { x, y } = bit_decombine(n % 4 ** d);
        const s = 1 / 2 ** d;
        const col = parent % ALLSKY_COLS, row = Math.floor(parent / ALLSKY_COLS);
        rect = [(col * THUMB + y * s * THUMB) / ALLSKY_W, (row * THUMB + x * s * THUMB) / ALLSKY_H, (s * THUMB) / ALLSKY_W, (s * THUMB) / ALLSKY_H];
        tex = this.allsky;
        texW = ALLSKY_W;
        texH = ALLSKY_H;
      }
      if (!tex) continue;
      // Stay half a texel inside, so neighbouring thumbnails in the preview do not bleed into each other.
      const iu = 0.5 / texW, iv = 0.5 / texH;
      const r: [number, number, number, number] = [rect[0] + iu, rect[1] + iv, rect[2] - 2 * iu, rect[3] - 2 * iv];
      const m = this.mesh(K, n);
      gl.bindTexture(gl.TEXTURE_2D, tex);
      p.f4('u_uvRect', r[0], r[1], r[2], r[3]);
      gl.bindVertexArray(m.vao);
      gl.drawElements(gl.TRIANGLES, m.count, gl.UNSIGNED_SHORT, 0);
    }
    gl.bindVertexArray(null);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  }
}
